import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import Seo from "../Components/Seo";

const API_BASE = API_BASE_URL;

const Otp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mobileNumber = location?.state?.mobileNumber || "";
  const firstTime = location?.state?.firstTime;
  const fcmToken = location?.state?.fcmToken || "web-client";
  const hashCode = location?.state?.hashCode || "web-hash";

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [timer, setTimer] = useState(45);
  const [resendAllowed, setResendAllowed] = useState(false);

  const inputRef = useRef(null);
  const autoSubmitting = useRef(false);

  useEffect(() => {
    if (!mobileNumber) {
      navigate("/login");
    }
  }, [mobileNumber, navigate]);

  useEffect(() => {
    const id = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          setResendAllowed(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const verifyWithBackend = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobileNo: mobileNumber,
          otp,
        }),
      });
      const data = await res.json().catch(() => ({}));
      const msg = String(data?.message || data?.Message || "").toLowerCase();

      if (res.ok) {
        if (msg.includes("success") || data?.success === true || data?.status === "success") {
          if (data?.token) {
            localStorage.setItem("authToken", data.token);
          }
          return true;
        }
        throw new Error(data?.message || data?.Message || "Invalid OTP");
      }

      // Server ne galat OTP par 4xx/5xx diya -> definitely failure
      throw new Error(data?.message || data?.Message || `Verification failed (${res.status})`);
    } catch (e) {
      // No client-side bypass: sirf backend hi OTP verify kar sakta hai.
      throw new Error(e?.message || "Verification failed. Please try again.");
    }
  }, [mobileNumber, otp]);

  const submitOtp = useCallback(async () => {
    if (autoSubmitting.current || loading) return;
    setError("");
    setSuccess("");
    if (!/^\d{4}$/.test(otp)) {
      setError("Please enter the 4-digit OTP.");
      return;
    }
    try {
      autoSubmitting.current = true;
      setLoading(true);
      await verifyWithBackend();
      localStorage.setItem("userMobileNumber", mobileNumber);
      if (firstTime !== undefined) {
        localStorage.setItem("firstTime", String(firstTime));
      }
      setSuccess("OTP verified. Redirecting...");
      setTimeout(() => navigate("/"), 600);
    } catch (err) {
      setError(err.message || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
      autoSubmitting.current = false;
    }
  }, [otp, loading, mobileNumber, firstTime, navigate, verifyWithBackend]);

  const handleVerify = async (e) => {
    e.preventDefault();
    await submitOtp();
  };

  useEffect(() => {
    if (/^\d{4}$/.test(otp)) {
      submitOtp();
    }
  }, [otp, submitOtp]);

  const handleResend = async () => {
    if (!resendAllowed) return;
    setError("");
    setSuccess("");
    setResendAllowed(false);
    setTimer(45);
    try {
      const res = await fetch(`${API_BASE}/otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobileNo: mobileNumber,
          FCM: fcmToken,
          hash: hashCode,
        }),
      });
      if (!res.ok) throw new Error(`Resend failed (${res.status})`);
      const data = await res.json();
      if (String(data?.message || data?.Message || "").toLowerCase().includes("otp sent")) {
        setSuccess("OTP resent successfully");
      } else {
        throw new Error(data?.message || data?.Message || "Failed to resend OTP");
      }
    } catch (err) {
      setError(err.message || "Unable to resend OTP.");
      setResendAllowed(true);
    }
  };

  return (
    <div style={styles.page}>
      <Seo title="Verify OTP" description="AIO India login ke liye OTP verify karein — Instant mobile OTP verification." path="/otp" />
      <style>{keyframes}</style>
      <div style={styles.card}>
        <div style={styles.badge}>Step 2 of 2</div>
        <h1 style={styles.title}>Enter OTP</h1>
        <p style={styles.subtitle}>
          We sent a 4-digit code to <strong style={{ color: "#e2e8f0" }}>+91 {mobileNumber}</strong>
        </p>

        <form onSubmit={handleVerify} style={styles.form}>
          <label style={styles.label}>OTP</label>
          <input
            ref={inputRef}
            style={styles.otpInput}
            value={otp}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 4);
              setOtp(v);
            }}
            inputMode="numeric"
            placeholder="4-digit code"
            maxLength={4}
            required
          />

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}

          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? "Verifying..." : "Verify & Continue"}
          </button>
        </form>

        <div style={styles.footerRow}>
          <span style={styles.timer}>{resendAllowed ? "Didn’t get it?" : `Resend in ${timer}s`}</span>
          <button
            style={{ ...styles.linkButton, opacity: resendAllowed ? 1 : 0.5 }}
            type="button"
            onClick={handleResend}
            disabled={!resendAllowed}
          >
            Resend OTP
          </button>
        </div>

        <button style={styles.linkButton} type="button" onClick={() => navigate("/login")}>Change number</button>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "90vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #0f1b2d 0%, #122a68 50%, #0f1b2d 100%)",
    padding: "32px",
  },
  card: {
    width: "100%",
    maxWidth: "420px",
    background: "#0f172a",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "32px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
    color: "#e2e8f0",
    position: "relative",
    overflow: "hidden",
    animation: "fadeInUp 600ms ease",
  },
  badge: {
    display: "inline-block",
    padding: "6px 12px",
    background: "rgba(59,130,246,0.15)",
    color: "#93c5fd",
    borderRadius: "999px",
    fontSize: "12px",
    letterSpacing: "0.5px",
    marginBottom: "14px",
  },
  title: { margin: 0, fontSize: "26px", fontWeight: 700 },
  subtitle: { margin: "6px 0 18px", color: "#94a3b8", fontSize: "15px" },
  form: { display: "flex", flexDirection: "column", gap: "14px" },
  label: { fontSize: "14px", color: "#cbd5e1" },
  otpInput: {
    width: "100%",
    padding: "14px 16px",
    fontSize: "22px",
    letterSpacing: "8px",
    textAlign: "center",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.04)",
    color: "#e2e8f0",
    outline: "none",
  },
  button: {
    marginTop: "6px",
    width: "100%",
    padding: "12px 16px",
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    background: "linear-gradient(135deg, #22c55e, #16a34a)",
    color: "white",
    fontWeight: 700,
    letterSpacing: "0.3px",
    boxShadow: "0 10px 30px rgba(34,197,94,0.32)",
    transition: "transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease",
  },
  error: {
    background: "rgba(248,113,113,0.12)",
    color: "#fecdd3",
    padding: "10px 12px",
    borderRadius: "10px",
    fontSize: "13px",
    border: "1px solid rgba(248,113,113,0.35)",
  },
  success: {
    background: "rgba(74,222,128,0.12)",
    color: "#bbf7d0",
    padding: "10px 12px",
    borderRadius: "10px",
    fontSize: "13px",
    border: "1px solid rgba(74,222,128,0.35)",
  },
  footerRow: {
    marginTop: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    color: "#94a3b8",
    fontSize: "13px",
  },
  timer: { color: "#cbd5e1" },
  linkButton: {
    background: "transparent",
    border: "none",
    color: "#93c5fd",
    cursor: "pointer",
    padding: 0,
    fontWeight: 600,
  },
};

const keyframes = `
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
button:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(34,197,94,0.42); }
button:disabled { opacity: 0.7; cursor: not-allowed; }
`;

export default Otp;

